import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type IChatThreadState, useChatThread } from '../../hooks/useChatThread';
import { type ISupportSession } from '../../hooks/useSupportSession';
import { type ChatThreadRef, localThreadId } from '../../lib/chat-thread-ref';
import { publishThreadState, removeThreadState } from '../../lib/chat-thread-store';
import { peerChatEnabled } from '../../lib/peer-chat-flags';
import { itemsForThread } from '../../lib/support-outbox';

/** How many peer engines stay mounted. A ward agent talks to a few children at a time, not forty. */
export const MAX_HOSTED_PEER_THREADS = 8;

export interface IPeerThreads {
	/** Ask the layout to host (or keep hosting) a conversation. Idempotent. */
	openThread: (ref: ChatThreadRef) => void;
	openRefs: ChatThreadRef[];
}

/**
 * The layout's list of hosted peer conversations, least-recently-opened first.
 *
 * <p>Opening an already-hosted thread moves it to the end; opening a new one past the cap
 * drops the oldest. The engine for a dropped thread unmounts cleanly — its outbox items stay
 * in storage and drain again the next time it is opened.</p>
 */
export interface IPeerThreadRefsOptions {
	/** The conversation whose panel is on screen. Never evicted. */
	activeLocalId?: string | null;
	/** The reader's id, to check the outbox: a thread with queued messages is never evicted. */
	me?: number;
}

export const usePeerThreadRefs = ({ activeLocalId = null, me = 0 }: IPeerThreadRefsOptions = {}): IPeerThreads => {
	const [openRefs, setOpenRefs] = useState<ChatThreadRef[]>([]);
	// Read through refs so `openThread` stays referentially stable: it is in a context value.
	const activeRef = useRef(activeLocalId);
	activeRef.current = activeLocalId;
	const meRef = useRef(me);
	meRef.current = me;

	const openThread = useCallback((ref: ChatThreadRef) => {
		if (ref.kind !== 'peer' || !peerChatEnabled()) return;
		const id = localThreadId(ref);
		setOpenRefs((current) => {
			const last = current[current.length - 1];
			// Already the most recent: return the same array so nothing downstream re-renders.
			if (last && localThreadId(last) === id) return current;
			const next = [...current.filter((r) => localThreadId(r) !== id), ref];
			return trimHosted(next, activeRef.current, meRef.current);
		});
	}, []);

	return useMemo(() => ({ openThread, openRefs }), [openThread, openRefs]);
};

/**
 * Drop the oldest hosted threads past the cap — but never the one on screen, and never one
 * with messages still queued (an engine must stay mounted to drain its slice of the outbox).
 * If nothing qualifies, the list grows past the cap rather than losing a send.
 */
export const trimHosted = (refs: ChatThreadRef[], activeLocalId: string | null, me: number): ChatThreadRef[] => {
	let excess = refs.length - MAX_HOSTED_PEER_THREADS;
	if (excess <= 0) return refs;

	const kept: ChatThreadRef[] = [];
	for (const ref of refs) {
		const id = localThreadId(ref);
		const pinned = id === activeLocalId || (me > 0 && itemsForThread(me, id).length > 0);
		if (excess > 0 && !pinned) {
			excess -= 1;
			continue;
		}
		kept.push(ref);
	}
	return kept;
};

interface IHostProps {
	threadRef: ChatThreadRef;
	session: ISupportSession;
	isActive: boolean;
}

/** The fields a panel or badge can see change. Callbacks on the state are stable and are not compared. */
const PUBLISHED_FIELDS: readonly (keyof IChatThreadState)[] = [
	'threadKey',
	'messages',
	'unread',
	'reach',
	'sending',
	'liveStatus',
	'online',
	'otherIsTyping',
	'composerDisabled',
	'storageFull',
	'pendingAttachmentCount',
	'uploadPercents',
	'needsReattach',
	'thumbnails',
];

const sameForReaders = (a: IChatThreadState | null, b: IChatThreadState): boolean => {
	if (!a) return false;
	for (const field of PUBLISHED_FIELDS) {
		const x = a[field];
		const y = b[field];
		if (x === y) continue;
		// `needsReattach` and `thumbnails` are rebuilt every render; compare their contents.
		if (x instanceof Set && y instanceof Set) {
			if (x.size !== y.size || [...x].some((v) => !y.has(v))) return false;
			continue;
		}
		if (field === 'thumbnails' && typeof x === 'object' && typeof y === 'object' && x && y) {
			const xs = Object.entries(x);
			const ys = y as Record<string, string>;
			if (xs.length !== Object.keys(ys).length || xs.some(([k, v]) => ys[k] !== v)) return false;
			continue;
		}
		return false;
	}
	return true;
};

/**
 * One engine, publishing into the store the panel reads whenever something a reader can see
 * has changed.
 *
 * <p>Renders nothing. The engine hands back a new state object every render, so publishing
 * unconditionally would notify the store on renders that changed nothing — and a reader in
 * the layout (the FAB badge) re-rendering the layout re-renders this host, which would publish
 * again, forever. The shallow compare on the readable fields is what breaks that cycle; the
 * component is memoised so an unrelated layout render does not reach the engine at all.</p>
 */
const PeerThreadHost = memo(({ threadRef, session, isActive }: IHostProps) => {
	const state = useChatThread(threadRef, { session, isActive });
	const localId = localThreadId(threadRef);
	const lastPublishedRef = useRef<IChatThreadState | null>(null);

	useEffect(() => {
		if (sameForReaders(lastPublishedRef.current, state)) return;
		lastPublishedRef.current = state;
		publishThreadState(localId, state);
	});

	useEffect(
		() => () => {
			lastPublishedRef.current = null;
			removeThreadState(localId);
		},
		[localId]
	);

	return null;
});
PeerThreadHost.displayName = 'PeerThreadHost';

interface IPeerThreadHostsProps {
	refs: ChatThreadRef[];
	session: ISupportSession;
	/** The local id of the conversation whose panel is on screen, if any. */
	activeLocalId: string | null;
}

/** Mounts one {@link PeerThreadHost} per hosted ref. Lives beside the support engine in the layout. */
const PeerThreadHosts = ({ refs, session, activeLocalId }: IPeerThreadHostsProps) => (
	<>
		{refs.map((ref) => {
			const id = localThreadId(ref);
			return (
				<PeerThreadHost
					key={id}
					threadRef={ref}
					session={session}
					isActive={activeLocalId === id}
				/>
			);
		})}
	</>
);

export default PeerThreadHosts;
